(() => {
  const STATES = ['stock', 'preorder', 'remaining_stock', 'closed', 'sold_out', 'pending'];
  const DEFAULT_STATES = ['stock', 'preorder', 'remaining_stock', 'closed', 'sold_out'];
  const SELLING_STATES = ['stock', 'preorder', 'remaining_stock'];
  const SORTS = ['manual', 'best-selling', 'title-ascending', 'title-descending', 'price-ascending', 'price-descending', 'created-ascending', 'created-descending'];
  const DEFAULT_LOCALE = {
    loadError: '商品を読み込めませんでした。',
    loadFailed: '読み込み失敗',
    priceNumber: '価格は0以上の整数で入力してください。',
    priceRange: '最高価格は最低価格以上にしてください。',
    showingCount: '{{ start }}–{{ end }} / {{ total }}件',
    dialogCount: '（{{ total }}件）',
    page: '{{ page }}ページ',
    previous: '前のページ',
    next: '次のページ'
  };
  const formatLabel = (template, values) => String(template || '').replace(/{{\s*([a-z]+)\s*}}|__ASTALIE_([A-Z]+)__/gi, (_, key, sentinel) => values[key || sentinel.toLowerCase()] ?? '');
  const numberOrNull = value => value === null || value === '' || !Number.isFinite(Number(value)) ? null : Math.max(0, Number(value));

  function readState(url, salesPaused = false) {
    const params = url.searchParams;
    let statuses;
    if (params.has('status')) statuses = params.getAll('status').flatMap(value => value.split(',')).filter(value => STATES.includes(value));
    else if (salesPaused) statuses = STATES.slice();
    else if (params.has('filter.v.availability')) {
      const legacy = params.getAll('filter.v.availability');
      statuses = [...(legacy.includes('1') ? SELLING_STATES : []), ...(legacy.includes('0') ? ['closed', 'sold_out', 'pending'] : [])];
    } else statuses = DEFAULT_STATES.slice();
    return {
      statuses: [...new Set(statuses)],
      min: numberOrNull(params.get('min_price') ?? params.get('filter.v.price.gte')),
      max: numberOrNull(params.get('max_price') ?? params.get('filter.v.price.lte')),
      sort: SORTS.includes(params.get('sort_by')) ? params.get('sort_by') : 'created-descending',
      page: Math.max(1, Number.parseInt(params.get('catalog_page') || params.get('page') || '1', 10) || 1),
    };
  }

  function stateURL(base, state) {
    const url = new URL(base);
    for (const key of ['status', 'page', 'filter.v.availability', 'filter.v.price.gte', 'filter.v.price.lte']) url.searchParams.delete(key);
    url.searchParams.set('status', state.statuses.length ? state.statuses.join(',') : 'none');
    for (const [key, value] of [['min_price', state.min], ['max_price', state.max]]) {
      if (value === null) url.searchParams.delete(key); else url.searchParams.set(key, String(value));
    }
    url.searchParams.set('sort_by', state.sort);
    if (state.page > 1) url.searchParams.set('catalog_page', String(state.page)); else url.searchParams.delete('catalog_page');
    return url;
  }

  function selectProducts(products, state, pageSize) {
    const withinPrice = products.filter(product => product.prices.some(price => (state.min === null || price >= state.min * 100) && (state.max === null || price <= state.max * 100)));
    const counts = Object.fromEntries(STATES.map(status => [status, withinPrice.filter(product => product.status === status).length]));
    const selected = withinPrice.filter(product => state.statuses.includes(product.status));
    const descending = state.sort.endsWith('-descending');
    const compare = state.sort.startsWith('price-') ? (a, b) => a.price - b.price
      : state.sort.startsWith('created-') ? (a, b) => a.created - b.created
      : state.sort.startsWith('title-') ? (a, b) => a.title.localeCompare(b.title, 'ja')
      : (a, b) => a.rank - b.rank;
    selected.sort((a, b) => (descending ? -1 : 1) * compare(a, b) || a.rank - b.rank);
    const pages = Math.ceil(selected.length / pageSize);
    const page = Math.min(Math.max(1, state.page), Math.max(1, pages));
    return { counts, total: selected.length, pages, page, items: selected.slice((page - 1) * pageSize, page * pageSize) };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { readState, stateURL, selectProducts, STATES };
  if (typeof window === 'undefined') return;

  class AstalieCatalog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.state = readState(new URL(location.href), this.dataset.salesPaused === 'true');
      this.pageSize = Number(this.dataset.pageSize) || 18;
      this.form = this.querySelector('[data-catalog-form]');
      this.fields = this.querySelector('[data-catalog-fields]');
      this.results = this.querySelector('.daiko-catalog__results');
      this.grid = this.querySelector('#product-grid');
      this.dialog = this.querySelector('[data-catalog-dialog]');
      this.controls = this.querySelector('[data-catalog-controls]');
      this.locale = Object.fromEntries(Object.entries({
        loadError: this.dataset.catalogLabelLoadError,
        loadFailed: this.dataset.catalogLabelLoadFailed,
        priceNumber: this.dataset.catalogLabelPriceNumber,
        priceRange: this.dataset.catalogLabelPriceRange,
        showingCount: this.dataset.catalogLabelShowingCount,
        dialogCount: this.dataset.catalogLabelDialogCount,
        page: this.dataset.catalogLabelPage,
        previous: this.dataset.catalogLabelPrevious,
        next: this.dataset.catalogLabelNext
      }).map(([key, value]) => [key, value || DEFAULT_LOCALE[key]]));
      this.media = matchMedia('(min-width: 1200px)');
      this.onResize = () => this.placeControls();
      this.onInput = event => {
        if (!event.target.closest('[data-catalog-form]')) return;
        clearTimeout(this.inputTimer);
        if (event.target.name === 'sort_by' || event.target.name === 'status') this.applyForm();
        else this.inputTimer = setTimeout(() => this.applyForm(), 250);
      };
      this.onSubmit = event => { event.preventDefault(); clearTimeout(this.inputTimer); this.applyForm(); };
      this.onClick = event => this.handleClick(event);
      this.onPopstate = () => {
        const previousSort = this.state.sort;
        this.state = readState(new URL(location.href), this.dataset.salesPaused === 'true');
        this.syncForm();
        if (this.requiresServerSort(previousSort)) this.load(); else if (this.products) this.render('replace');
      };
      this.onDialogClose = () => {
        this.querySelector('[data-catalog-open]').setAttribute('aria-expanded', 'false');
        if (!this.media.matches || this.dataset.filterType === 'drawer') this.querySelector('[data-catalog-open]').focus();
      };
      this.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopstate);
      this.media.addEventListener('change', this.onResize);
      this.dialog.addEventListener('close', this.onDialogClose);
      this.placeControls();
      this.syncForm();
      this.load();
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.inputTimer);
      this.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopstate);
      this.media?.removeEventListener('change', this.onResize);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      if (this.dialog?.open) this.dialog.close();
      this.initialized = false;
    }

    placeControls() {
      const desktop = this.media.matches && this.dataset.filterType !== 'drawer';
      if (desktop && this.dialog.open) this.dialog.close();
      const host = this.querySelector(desktop ? '[data-catalog-sidebar-host]' : '[data-catalog-mobile-host]');
      if (this.controls.parentElement !== host) host.append(this.controls);
      this.classList.toggle('daiko-catalog--drawer', this.dataset.filterType === 'drawer');
    }

    sourceURL(page = 1) {
      const url = new URL(location.href);
      for (const key of ['status', 'catalog_page', 'min_price', 'max_price', 'filter.v.availability', 'filter.v.price.gte', 'filter.v.price.lte']) url.searchParams.delete(key);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('page', String(page));
      // Native ranking is needed for these two sorts; other sorts use the same complete snapshot.
      url.searchParams.set('sort_by', ['manual', 'best-selling'].includes(this.state.sort) ? this.state.sort : 'created-descending');
      return url;
    }

    async load() {
      this.abort?.abort();
      const abort = new AbortController();
      this.abort = abort;
      this.fields.disabled = true;
      this.results.setAttribute('aria-busy', 'true');
      this.querySelector('[data-catalog-loading]').hidden = false;
      this.querySelector('[data-catalog-error]').hidden = true;
      try {
        const sourceSort = ['manual', 'best-selling'].includes(this.state.sort) ? this.state.sort : 'created-descending';
        const products = [], seen = new Set();
        let sourcePage = 1, pages = 1, nextURL = this.sourceURL(1);
        const visited = new Set();
        do {
          if (visited.has(nextURL.href) || sourcePage > 100) throw new Error('Catalog continuation repeats');
          visited.add(nextURL.href);
          const response = await fetch(nextURL, { signal: abort.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`Catalog HTTP ${response.status}`);
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const batch = document.querySelector('template[data-catalog-batch]');
          if (!batch || Number(batch.dataset.page) !== sourcePage) throw new Error('Catalog page missing');
          pages = Math.max(1, Number(batch.dataset.pages) || 1);
          if (Number(batch.dataset.total) > 25000) throw new Error('Catalog exceeds Shopify pagination limit');
          const nodes = [...batch.content.querySelectorAll('[data-catalog-product]')];
          if (sourcePage < pages && !batch.dataset.next) throw new Error('Catalog continuation missing');
          for (const node of nodes) {
            const id = node.dataset.productId;
            if (!id || seen.has(id)) continue;
            seen.add(id);
            products.push({ id, status: node.dataset.storefrontState, title: node.dataset.title, price: Number(node.dataset.price), prices: JSON.parse(node.dataset.prices), created: Number(node.dataset.created), rank: products.length, node });
          }
          if (sourcePage < pages) {
            nextURL = new URL(batch.dataset.next, response.url);
            if (nextURL.origin !== location.origin || nextURL.pathname !== location.pathname) throw new Error('Invalid catalog continuation');
            nextURL.searchParams.set('section_id', this.dataset.sectionId);
          }
          sourcePage++;
        } while (sourcePage <= pages);
        if (abort.signal.aborted) return;
        this.products = products;
        this.serverSort = sourceSort;
        this.render('replace');
      } catch (error) {
        if (abort.signal.aborted) return;
        this.querySelector('[data-catalog-error]').hidden = false;
        // Never turn an incomplete batch into a plausible count or page list.
        if (!this.products) this.querySelectorAll('[data-catalog-count]').forEach(el => { el.textContent = this.locale.loadFailed; });
      } finally {
        if (!abort.signal.aborted) {
          this.fields.disabled = !this.products;
          this.results.setAttribute('aria-busy', 'false');
          this.querySelector('[data-catalog-loading]').hidden = true;
        }
      }
    }

    requiresServerSort(previous) {
      return this.state.sort !== previous && ['manual', 'best-selling'].includes(this.state.sort) && this.serverSort !== this.state.sort;
    }

    syncForm() {
      this.form.querySelectorAll('[name="status"]').forEach(input => { input.checked = this.state.statuses.includes(input.value); });
      for (const [name, value] of [['min_price', this.state.min], ['max_price', this.state.max], ['sort_by', this.state.sort]]) {
        const input = this.form.elements.namedItem(name);
        if (input) { input.value = value ?? ''; input.removeAttribute('aria-invalid'); }
      }
      const options = [...this.querySelectorAll('[data-daiko-sort-option]')];
      options.forEach(option => option.setAttribute('aria-selected', String(option.dataset.value === this.state.sort)));
      const option = options.find(option => option.dataset.value === this.state.sort);
      const label = this.querySelector('[data-daiko-sort-label]');
      if (label && option) label.textContent = option.textContent.trim();
    }

    applyForm() {
      const data = new FormData(this.form);
      const min = numberOrNull(data.get('min_price')), max = numberOrNull(data.get('max_price'));
      const invalidNumber = ['min_price', 'max_price'].some(name => { const input = this.form.elements.namedItem(name); return input && !input.validity.valid; });
      const invalid = invalidNumber || (min !== null && max !== null && min > max);
      const error = this.querySelector('[data-catalog-price-error]');
      if (error) { error.hidden = !invalid; error.textContent = invalidNumber ? this.locale.priceNumber : this.locale.priceRange; }
      for (const name of ['min_price', 'max_price']) this.form.elements.namedItem(name)?.setAttribute('aria-invalid', String(invalid));
      if (invalid) return;
      const previousSort = this.state.sort;
      this.state = { ...this.state, statuses: this.form.querySelector('[name="status"]') ? data.getAll('status') : this.state.statuses, min, max, sort: data.get('sort_by') || this.state.sort, page: 1 };
      if (this.requiresServerSort(previousSort)) {
        history.pushState({}, '', stateURL(location.href, this.state));
        this.load();
      } else if (this.products) this.render('push');
    }

    handleClick(event) {
      if (event.target.closest('[data-catalog-open]')) {
        this.dialog.showModal();
        this.querySelector('[data-catalog-open]').setAttribute('aria-expanded', 'true');
      } else if (event.target.closest('[data-catalog-close]')) this.dialog.close();
      else if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      } else if (event.target.closest('[data-catalog-reset]')) {
        this.state = { statuses: this.dataset.salesPaused === 'true' ? STATES.slice() : DEFAULT_STATES.slice(), min: null, max: null, sort: 'created-descending', page: 1 };
        const priceError = this.querySelector('[data-catalog-price-error]');
        if (priceError) priceError.hidden = true;
        this.syncForm();
        if (this.products) this.render('push');
      } else if (event.target.closest('[data-catalog-retry]')) this.load();
      else {
        const link = event.target.closest('[data-catalog-page]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.state.page = Number(link.dataset.catalogPage);
        this.render('push');
        this.grid.focus({ preventScroll: true });
        const header = document.querySelector('.shopify-section-header-sticky');
        const offset = header?.getBoundingClientRect().height || 90;
        window.scrollTo({ top: this.results.getBoundingClientRect().top + scrollY - offset - 16, behavior: 'instant' });
      }
    }

    render(historyMode) {
      const result = selectProducts(this.products, this.state, this.pageSize);
      this.state.page = result.page;
      this.grid.replaceChildren(...result.items.map(product => product.node.cloneNode(true)));
      const start = result.total ? (result.page - 1) * this.pageSize + 1 : 0;
      const end = Math.min(result.page * this.pageSize, result.total);
      this.querySelectorAll('[data-catalog-count]').forEach(el => { el.textContent = formatLabel(this.locale.showingCount, { start, end, total: result.total }); });
      this.querySelector('[data-catalog-dialog-count]').textContent = formatLabel(this.locale.dialogCount, { total: result.total });
      for (const [status, count] of Object.entries(result.counts)) {
        const el = this.querySelector(`[data-catalog-state-count="${status}"]`);
        if (el) el.textContent = `(${count})`;
      }
      const pending = this.querySelector('[data-catalog-pending]');
      if (pending) pending.hidden = !this.products.some(product => product.status === 'pending');
      const maxInput = this.form.elements.namedItem('max_price');
      if (maxInput) maxInput.placeholder = String(Math.ceil(Math.max(0, ...this.products.map(product => Math.max(...product.prices))) / 100));
      this.querySelector('[data-catalog-empty]').hidden = result.total !== 0;
      this.renderPagination(result.pages);
      const url = stateURL(location.href, this.state);
      if (url.href !== location.href) history[historyMode === 'push' ? 'pushState' : 'replaceState']({}, '', url);
      this.querySelectorAll('[data-catalog-ip]').forEach(link => {
        const target = stateURL(link.href, { ...this.state, page: 1 });
        link.href = target;
      });
      this.dataset.ready = 'true';
      this.dispatchEvent(new CustomEvent('catalog:render', { detail: { total: result.total, page: result.page, pages: result.pages } }));
    }

    renderPagination(pages) {
      const nav = this.querySelector('[data-catalog-pagination]');
      nav.replaceChildren();
      nav.hidden = pages <= 1;
      if (pages <= 1) return;
      const addLink = (page, label, aria) => {
        const link = document.createElement('a');
        link.textContent = label;
        if (aria) {
          const icon = this.querySelector('[data-catalog-page-icon]').content.cloneNode(true);
          link.replaceChildren(icon);
          link.classList.add(page < this.state.page ? 'daiko-catalog__previous' : 'daiko-catalog__next');
        }
        link.href = stateURL(location.href, { ...this.state, page });
        link.dataset.catalogPage = String(page);
        link.setAttribute('aria-label', aria || formatLabel(this.locale.page, { page }));
        if (page === this.state.page) link.setAttribute('aria-current', 'page');
        nav.append(link);
      };
      if (this.state.page > 1) addLink(this.state.page - 1, '‹', this.locale.previous);
      const visible = new Set([1, pages]);
      for (let page = this.state.page - 2; page <= this.state.page + 2; page++) if (page > 0 && page <= pages) visible.add(page);
      let last = 0;
      for (const page of [...visible].sort((a, b) => a - b)) {
        if (last && page - last > 1) { const dots = document.createElement('span'); dots.textContent = '…'; dots.setAttribute('aria-hidden', 'true'); nav.append(dots); }
        addLink(page, String(page)); last = page;
      }
      if (this.state.page < pages) addLink(this.state.page + 1, '›', this.locale.next);
    }
  }
  if (!customElements.get('astalie-catalog')) customElements.define('astalie-catalog', AstalieCatalog);
})();
  if (typeof window !== 'undefined' && !customElements.get('daiko-sort-menu')) {
    customElements.define(
      'daiko-sort-menu',
      class DaikoSortMenu extends HTMLElement {
        connectedCallback() {
          if (this.dataset.ready === 'true') return;
          this.dataset.ready = 'true';
          this.trigger = this.querySelector('[data-daiko-sort-trigger]');
          this.menu = this.querySelector('[data-daiko-sort-options]');
          this.input =
            this.querySelector('[data-daiko-sort-input]') ||
            this.closest('form')?.querySelector('[data-daiko-sort-input]');
          this.label = this.querySelector('[data-daiko-sort-label]');
          this.options = Array.from(this.querySelectorAll('[data-daiko-sort-option]'));
          if (!this.trigger || !this.menu || !this.input || !this.options.length) return;

          this.onTriggerClick = () => this.toggle();
          this.onTriggerKeydown = (event) => {
            if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
            event.preventDefault();
            this.open(event.key === 'ArrowUp' ? this.options.length - 1 : this.selectedIndex());
          };
          this.onMenuClick = (event) => {
            const option = event.target.closest('[data-daiko-sort-option]');
            if (option) this.select(option);
          };
          this.onMenuKeydown = (event) => this.handleMenuKeydown(event);
          this.onDocumentClick = (event) => {
            if (!this.contains(event.target)) this.close();
          };

          this.trigger.addEventListener('click', this.onTriggerClick);
          this.trigger.addEventListener('keydown', this.onTriggerKeydown);
          this.menu.addEventListener('click', this.onMenuClick);
          this.menu.addEventListener('keydown', this.onMenuKeydown);
          document.addEventListener('click', this.onDocumentClick);
        }

        disconnectedCallback() {
          this.trigger?.removeEventListener('click', this.onTriggerClick);
          this.trigger?.removeEventListener('keydown', this.onTriggerKeydown);
          this.menu?.removeEventListener('click', this.onMenuClick);
          this.menu?.removeEventListener('keydown', this.onMenuKeydown);
          document.removeEventListener('click', this.onDocumentClick);
          delete this.dataset.ready;
        }

        selectedIndex() {
          const index = this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
          return index < 0 ? 0 : index;
        }

        open(focusIndex = this.selectedIndex()) {
          this.menu.hidden = false;
          this.trigger.setAttribute('aria-expanded', 'true');
          this.options[focusIndex]?.focus();
        }

        close({ restoreFocus = false } = {}) {
          if (!this.menu || this.menu.hidden) return;
          this.menu.hidden = true;
          this.trigger.setAttribute('aria-expanded', 'false');
          if (restoreFocus) this.trigger.focus();
        }

        toggle() {
          if (this.menu.hidden) this.open();
          else this.close({ restoreFocus: true });
        }

        select(option) {
          const value = option.dataset.value || '';
          this.options.forEach((item) => item.setAttribute('aria-selected', item === option ? 'true' : 'false'));
          this.input.value = value;
          this.label.textContent = option.textContent.trim();
          this.close({ restoreFocus: true });
          const inputEventTarget = this.input.form && !this.input.closest('form') ? this.input.form : this.input;
          inputEventTarget.dispatchEvent(new Event('input', { bubbles: true }));
        }

        handleMenuKeydown(event) {
          const currentIndex = this.options.indexOf(document.activeElement);
          let nextIndex = currentIndex;
          if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.options.length;
          else if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.options.length) % this.options.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = this.options.length - 1;
          else if (event.key === 'Escape') {
            event.preventDefault();
            this.close({ restoreFocus: true });
            return;
          } else if (event.key === 'Tab') {
            this.close({ restoreFocus: true });
            return;
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (currentIndex >= 0) this.select(this.options[currentIndex]);
            return;
          } else return;
          event.preventDefault();
          this.options[nextIndex]?.focus();
        }
      }
    );
  }
